import React, { ReactNode } from 'react';
import { Button as MuiButton } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

type SizeTypes = 'small' | 'medium';
type VariantTypes = 'contained' | 'outlined' | 'text';
type ButtonTypes = 'primary' | 'inherited' | 'dark' | 'light';

interface ButtonProps {
  text: string | ReactNode;
  variant?: VariantTypes;
  type: ButtonTypes;
  size?: SizeTypes;
  disabled?: boolean;
  loading?: boolean;
  autoFocus?: boolean;
  className?: string;
  height?: number;
  width?: number;
  color?: string;
  backgroundColor?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement> | undefined;
  buttonRef?: (instance: HTMLButtonElement) => void | undefined;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  extraStyles?: any;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  id?: string;
  increaseWidth?: boolean;
}

/**
 * ButtonV2 Component
 *
 * A customizable button component built using Material-UI's Button.
 *
 * @component
 * @example
 * import ButtonV2 from './ButtonV2';
 *
 * // Contained Primary Button
 * <ButtonV2 text="Primary Button" type="primary" variant="contained" />
 *
 * // Contained Primary Button with Icon in start
 * import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
 * <ButtonV2 startIcon={<KeyboardArrowLeftIcon />} text="Primary" type="primary" variant="contained" size="medium" />
 *
 * @param {object} props - The component's properties.
 * @param {string|ReactNode} props.text - The text or content of the button.
 * @param {VariantTypes} [props.variant='contained'] - The button variant ('contained', 'outlined', or 'text').
 * @param {ButtonTypes} props.type - The button type ('primary' or 'inherited').
 * @param {SizeTypes} [props.size='medium'] - The button size ('small' or 'medium').
 * @param {boolean} [props.disabled=false] - Whether the button is disabled.
 * @param {boolean} [props.loading=false] - Whether the button is in loading state.
 * @param {boolean} [props.autoFocus=false] - Whether the button should autofocus.
 * @param {string} [props.className=''] - Additional CSS classes to apply to the button.
 * @param {number} [props.height=0] - The button's height (in pixels).
 * @param {number} [props.width=0] - The button's width (in pixels).
 * @param {string} [props.color=''] - The text color of the button.
 * @param {string} [props.backgroundColor=''] - The background color of the button.
 * @param {function} [props.onClick] - A callback function to handle the button's click event.
 * @param {function} [props.onKeyDown] - A callback function to handle keydown events on the button.
 * @param {function} [props.buttonRef] - A callback function to get a reference to the button element.
 * @param {ReactNode} [props.startIcon] - A Start Icon expects React Icon Element which will be rendered in button's start.
 * @param {ReactNode} [props.endIcon] - A End Icon expects React Icon Element which will be rendered in button's end.
 * @param {any} [props.extraStyles] - the extraStyles props expects any CSS rules for direct customization of button.
 * @param {string} [props.id] - the id of the button
 *
 * @returns {JSX.Element} The rendered ButtonV2 component.
 */

function ButtonV2({
  text = '',
  type = 'primary',
  variant = 'contained',
  size = 'medium',
  disabled = false,
  autoFocus = false,
  loading = false,
  className = '',
  height = 0,
  width = 0,
  color = '',
  backgroundColor = '',
  buttonRef = undefined,
  onClick = undefined,
  onKeyDown = undefined,
  startIcon = undefined,
  endIcon = undefined,
  onMouseEnter = undefined,
  onMouseLeave = undefined,
  onMouseDown = undefined,
  extraStyles = {},
  id,
  increaseWidth = false,
}: ButtonProps) {
  const textSizeCheck = typeof text === 'string' && text?.length >= 8;
  const styleOptions = {
    ...(width ? { width } : {}),
    ...(height ? { height } : {}),
    ...(backgroundColor ? { backgroundColor: `${backgroundColor} !important` } : {}),
    ...(color ? { color: `${color} !important` } : {}),
    ...((startIcon || endIcon) && textSizeCheck && !width ? { minWidth: increaseWidth? '115px !important':'110px !important' } : {}),
    ...(startIcon && endIcon && !width && textSizeCheck ? { minWidth: '120px !important' } : {}),
    textTransform: 'none',
    ...extraStyles,
  };

  const modifiedClassName = `custom-button custom-${variant}-${type} custom-${variant}-${size} ${
    className || ''
  }`;

  return (
    <MuiButton
      id={id}
      variant={variant}
      size={size}
      sx={styleOptions}
      onClick={onClick}
      onKeyDown={onKeyDown}
      disabled={loading || !!disabled}
      autoFocus={!!autoFocus}
      ref={buttonRef}
      className={modifiedClassName}
      startIcon={loading ? <CircularProgress color="inherit" size={16} /> : startIcon}
      endIcon={endIcon}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseDown={onMouseDown}
    >
      {text}
    </MuiButton>
  );
}

export default ButtonV2;
